import {computed, ref} from 'vue';
import {defineStore, storeToRefs} from 'pinia';
import {useLocalePrefix} from '#imports';
import {readonlyState} from '@malt/utils';
import type {
    FiltersResponse,
    RetrieveAvailableFiltersAdminFilterEnum,
    RetrieveAvailableFiltersExpEnum,
    RetrieveAvailableFiltersBadgeEnum,
    FetchError,
} from '@api';
import {useApis, useErrorHandler} from '~/composables';
import {useSearchUrlStore} from './search-url.store';

export interface StringArrayFilters {
    languages: string[];
    specialities: string[];
    categories: string[];
    businessSectors: string[];
}
export type StringArrayFilterName = keyof StringArrayFilters;

export interface SelectedFilters {
    languages: string[];
    specialities: string[];
    categories: string[];
    businessSectors: string[];
    badges: RetrieveAvailableFiltersBadgeEnum[];
    experienceLevels: RetrieveAvailableFiltersExpEnum[];
    adminFilter: RetrieveAvailableFiltersAdminFilterEnum[];
    minPrice?: number;
    maxPrice?: number;
}

export const useFiltersStore = defineStore('filters', () => {
    const searchUrlStore = useSearchUrlStore();
    const {searchUrlParams} = storeToRefs(searchUrlStore);
    const {handleSearchProfilesApiError} = useErrorHandler();
    const {searchApi} = useApis();
    const {getLocalePrefix, getLocaleCodeFromPrefix} = useLocalePrefix();
    const localePrefix = getLocalePrefix();
    const localeFromUrlPrefix = localePrefix ? getLocaleCodeFromPrefix(localePrefix) : undefined;

    const availableSearchFilters = ref<FiltersResponse>();
    const selectedFilters = computed<SelectedFilters>(() => {
        return {
            languages: searchUrlParams.value.languages,
            specialities: searchUrlParams.value.specialities,
            categories: searchUrlParams.value.categories,
            businessSectors: searchUrlParams.value.businessSectors,
            badges: searchUrlParams.value.badges,
            experienceLevels: searchUrlParams.value.experienceLevels,
            adminFilter: searchUrlParams.value.adminFilter,
            minPrice: searchUrlParams.value.minPrice,
            maxPrice: searchUrlParams.value.maxPrice,
        };
    });

    function isFilterChecked(filterName: StringArrayFilterName, value: string) {
        return searchUrlParams.value[filterName].includes(value);
    }

    function toggleFilter(filterName: StringArrayFilterName, value: string) {
        if (isFilterChecked(filterName, value)) {
            searchUrlParams.value[filterName] = searchUrlParams.value[filterName].filter((item) => item !== value);
        } else {
            searchUrlParams.value[filterName].push(value);
        }
        resetPageNumber();
    }

    function isBadgeChecked(value: RetrieveAvailableFiltersBadgeEnum) {
        return searchUrlParams.value.badges.includes(value);
    }

    function toggleBadgeFilter(value: RetrieveAvailableFiltersBadgeEnum) {
        if (isBadgeChecked(value)) {
            searchUrlParams.value.badges = searchUrlParams.value.badges.filter((item) => item !== value);
        } else {
            searchUrlParams.value.badges.push(value);
        }
        resetPageNumber();
    }

    function isExperienceLevelChecked(value: RetrieveAvailableFiltersExpEnum) {
        return searchUrlParams.value.experienceLevels.includes(value);
    }

    function toggleExperienceLevel(value: RetrieveAvailableFiltersExpEnum) {
        if (isExperienceLevelChecked(value)) {
            searchUrlParams.value.experienceLevels = searchUrlParams.value.experienceLevels.filter((item) => item !== value);
        } else {
            searchUrlParams.value.experienceLevels.push(value);
        }
        resetPageNumber();
    }

    function isAdminFilterChecked(value: RetrieveAvailableFiltersAdminFilterEnum) {
        return searchUrlParams.value.adminFilter.includes(value);
    }

    function toggleAdminFilter(value: RetrieveAvailableFiltersAdminFilterEnum) {
        if (isAdminFilterChecked(value)) {
            searchUrlParams.value.adminFilter = searchUrlParams.value.adminFilter.filter((item) => item !== value);
        } else {
            searchUrlParams.value.adminFilter.push(value);
        }
        resetPageNumber();
    }

    function setPriceMinFilter(min?: number) {
        searchUrlParams.value.minPrice = min;
        resetPageNumber();
    }

    function setPriceMaxFilter(max?: number) {
        searchUrlParams.value.maxPrice = max;
        resetPageNumber();
    }

    function resetFilters() {
        searchUrlParams.value = {
            ...searchUrlParams.value,
            languages: [],
            specialities: [],
            categories: [],
            businessSectors: [],
            badges: [],
            experienceLevels: [],
            adminFilter: [],
            minPrice: undefined,
            maxPrice: undefined,
            page: undefined,
        };
    }

    function resetPageNumber() {
        searchUrlParams.value.page = undefined;
    }

    async function fetchFilters() {
        const queryParams = searchUrlStore.toOpenApiRequestParams();

        try {
            availableSearchFilters.value = await searchApi.retrieveAvailableFilters({...queryParams, locale: localeFromUrlPrefix});
        } catch (error) {
            handleSearchProfilesApiError(error as FetchError);
        }
    }

    return {
        availableSearchFilters,
        selectedFilters: readonlyState(selectedFilters),
        fetchFilters,
        isAdminFilterChecked,
        isBadgeChecked,
        isExperienceLevelChecked,
        isFilterChecked,
        resetFilters,
        setPriceMaxFilter,
        setPriceMinFilter,
        toggleAdminFilter,
        toggleBadgeFilter,
        toggleExperienceLevel,
        toggleFilter,
    };
});
